import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Arrow, ArrowType, ComponentArrangementStyle, EdgeLabelPlacement, GraphComponent, GraphItemTypes, GraphViewerInputMode, HierarchicalLayout, IEdge, INode, LabelStyle, LayoutExecutor, LayoutOrientation, LayoutOrientationStringValues, Point, DashStyle, PolylineEdgeStyle, Rect, Size, Stroke, TimeSpan } from '@yfiles/yfiles';
import { renderNodeTip, textTip } from '../canvas/node-tooltip';
import { LENS_NODE_HEIGHT, LENS_NODE_WIDTH, LensGraphEdge, LensGraphNode, LensNodeStyle, LensNodeTag } from './lens-node-style';

// edges are drawn thicker as more passes along them, within these widths
const MIN_EDGE = 1.2;
const MAX_EDGE = 7;

// The graph a lens of the analysis screen draws: its nodes as cards, laid out top to bottom in the direction work
// flows, with edges thickened by how much passes along them. Selecting a node says which, so the screen can show it
@Component({
    selector: 'app-lens-graph',
    standalone: true,
    template: `
        <div class="pane">
            <header class="toolbar">
                <div><h1>{{ title }}</h1><span>{{ subtitle }}</span></div>
                <button type="button" (click)="fit()">Fit</button>
            </header>
            <div #host class="host" data-testid="lens-graph"></div>
        </div>
    `,
    // the same header as the Actions model's pane, so the views read alike
    styles: [`
        :host { display: block; position: relative; width: 100%; height: 100%; }
        .pane { display: flex; flex-direction: column; position: absolute; inset: 0; font-family: Inter, "Segoe UI", Arial, sans-serif; }
        .toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; padding: 12px 18px; border-bottom: 1px solid #e2e8f0; }
        .toolbar > div:first-child { flex: 1 0 auto; }
        .toolbar h1 { margin: 0; font-size: 18px; }
        .toolbar span { font-size: 12px; color: #64748b; }
        .toolbar button { padding: 7px 13px; border: 1px solid #cbd5e1; border-radius: 7px; background: #fff; cursor: pointer; }
        .host { position: relative; flex: 1 1 auto; min-height: 0; overflow: hidden; }
    `],
})
export class LensGraphComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('host', { static: true }) private host!: ElementRef<HTMLDivElement>;
    @Input() nodes: LensGraphNode[] = [];
    @Input() edges: LensGraphEdge[] = [];
    @Input() orientation: LayoutOrientation | LayoutOrientationStringValues = LayoutOrientation.TOP_TO_BOTTOM;
    @Input() title = '';
    @Input() subtitle = '';
    @Output() graphBuilt = new EventEmitter<GraphComponent>();
    @Output() nodeSelected = new EventEmitter<string | undefined>();

    graphComponent!: GraphComponent;
    private readonly nodeStyle = new LensNodeStyle();
    private readonly edgeLabelStyle = new LabelStyle({
        font: '11px Inter, "Segoe UI", Arial, sans-serif', textFill: '#475569', backgroundFill: 'rgba(255,255,255,0.85)', padding: 2,
    });
    private initialized = false;
    private fitWhenSized = false;    // the last fit was made before the canvas knew its size
    private generation = 0;
    private nodesByKey = new Map<string, INode>();

    ngAfterViewInit() {
        this.graphComponent = new GraphComponent(this.host.nativeElement);
        const inputMode = new GraphViewerInputMode({ selectableItems: GraphItemTypes.NODE, toolTipItems: GraphItemTypes.NODE | GraphItemTypes.EDGE | GraphItemTypes.LABEL });
        inputMode.toolTipInputMode.toolTipLocationOffset = new Point(15, 15);
        inputMode.toolTipInputMode.delay = TimeSpan.fromMilliseconds(50);
        inputMode.toolTipInputMode.duration = TimeSpan.fromSeconds(10);
        inputMode.addEventListener('query-item-tool-tip', evt => {
            if (evt.handled) return;
            const tag = evt.item?.tag;
            const lens = (tag as LensNodeTag)?.lens;
            const text = lens?.tooltip || (tag as { edge?: LensGraphEdge })?.edge?.tooltip;
            const tip = lens?.tip ? renderNodeTip(lens.tip) : text && textTip(text);
            if (!tip) return;
            evt.toolTip = tip;
            evt.handled = true;
        });
        inputMode.addEventListener('item-clicked', evt => {
            const lens = (evt.item?.tag as LensNodeTag)?.lens;
            this.nodeSelected.emit(lens?.key);
        });
        inputMode.addEventListener('canvas-clicked', () => this.nodeSelected.emit(undefined));
        this.graphComponent.inputMode = inputMode;
        this.graphComponent.addEventListener('size-changed', () => { if (this.fitWhenSized) this.fit(); });
        this.initialized = true;
        void this.build();
    }

    ngOnChanges(changes: SimpleChanges) {
        // a new direction, as when the user changes theirs, lays it out again
        if (this.initialized && (changes['nodes'] || changes['edges'] || (changes['orientation'] && !changes['orientation'].firstChange))) void this.build();
    }

    ngOnDestroy() {
        this.generation++;
        this.graphComponent?.cleanUp();
    }

    // shows the node and selects it
    async focus(key: string) {
        const node = this.nodesByKey.get(key);
        if (!node) return;
        this.graphComponent.selection.clear();
        this.graphComponent.selection.add(node);
        await this.graphComponent.zoomToAnimated(Math.max(this.graphComponent.zoom, 0.9), node.layout.center);
    }

    // redraws the nodes, as when a heatmap changes their fill
    refresh() {
        this.graphComponent?.invalidate();
    }

    fit() {
        if (!this.graphComponent) return;
        this.graphComponent.fitGraphBounds(40);
        // the canvas learns its size some time after it is shown (as when a dialog opens), and a fit made with a size it
        // hasn't caught up with leaves the graph out of view. Fit again once it has
        const { clientWidth, clientHeight } = this.host.nativeElement;
        const { width, height } = this.graphComponent.size;
        this.fitWhenSized = !clientWidth || !clientHeight || Math.round(width) !== clientWidth || Math.round(height) !== clientHeight;
    }

    private async build() {
        const gen = ++this.generation;
        const graph = this.graphComponent.graph;
        graph.clear();
        this.nodesByKey.clear();
        this.nodes.forEach(n => {
            const node = graph.createNode({ layout: new Rect(0, 0, LENS_NODE_WIDTH, LENS_NODE_HEIGHT), style: this.nodeStyle, tag: { lens: n } as LensNodeTag });
            this.nodesByKey.set(n.key, node);
        });
        const max = Math.max(1, ...this.edges.map(e => e.weight));
        this.edges.forEach(e => {
            const from = this.nodesByKey.get(e.from), to = this.nodesByKey.get(e.to);
            if (!from || !to) return;
            // thickness by the square root of the share of the most, so small hand-overs still show
            const width = MIN_EDGE + (MAX_EDGE - MIN_EDGE) * Math.sqrt(Math.max(0, e.weight) / max);
            const edge = graph.createEdge({
                source: from, target: to, tag: { edge: e },
                style: this.edgeStyle(width, e.colour || '#64748B', !!e.dashed),
            });
            if (e.label) graph.addLabel({ owner: edge, text: e.label, style: this.edgeLabelStyle, tag: { edge: e } });
        });
        if (graph.nodes.size) {
            // separate processes are packed into the shape of the view, rather than set side by side in one long row
            const view = this.graphComponent.size;
            const layout = new HierarchicalLayout({
                layoutOrientation: this.orientation, minimumLayerDistance: 70, nodeDistance: 40, stopDuration: '5s',
                // room is made for edge labels, so they sit on their edges clear of the cards
                edgeLabelPlacement: EdgeLabelPlacement.INTEGRATED,
                componentLayout: { style: ComponentArrangementStyle.PACKED_COMPACT_RECTANGLE, componentSpacing: 80, preferredSize: new Size(Math.max(400, view.width), Math.max(300, view.height)) },
            });
            await new LayoutExecutor({ graphComponent: this.graphComponent, layout, animationDuration: '0s' }).start();
        }
        if (gen !== this.generation) return;
        this.fit();
        this.graphBuilt.emit(this.graphComponent);
    }

    private edgeStyle(width: number, colour: string, dashed: boolean) {
        const stroke = new Stroke({ fill: colour, thickness: width, dashStyle: dashed ? new DashStyle([3, 2]) : DashStyle.SOLID });
        return new PolylineEdgeStyle({ stroke, targetArrow: new Arrow({ type: ArrowType.TRIANGLE, fill: colour, stroke: colour }) });
    }

    node(key: string): INode | undefined {
        return this.nodesByKey.get(key);
    }

    edgesOf(): IEdge[] {
        return this.graphComponent.graph.edges.toArray();
    }
}
